/**
 * installNativeApiInterceptor.js
 *
 * Monkey-patches `window.fetch` on Capacitor native (iOS/Android) so
 * that relative `/api/*` URLs resolve to the production Vercel
 * deployment instead of being served the SPA fallback index.html by
 * Capacitor's local WKURLSchemeHandler.
 *
 * Why this exists:
 * - On web, fetch('/api/anthropic') resolves to
 *   https://www.vinarai.com/api/anthropic — works.
 * - On Capacitor native, window.location.origin is
 *   capacitor://localhost, so fetch('/api/anthropic') resolves to
 *   capacitor://localhost/api/anthropic — a URL the WKWebView has no
 *   route for. The request never leaves the device.
 * - Earlier attempt (capacitor.config.json server.hostname =
 *   www.vinarai.com) seemed correct but broke too: Capacitor's local
 *   server intercepts ALL requests matching the configured hostname
 *   and returns the SPA fallback for paths not in webDir. So /api/*
 *   would get HTML instead of the real API.
 *
 * The fix:
 * - Leave Capacitor's origin as the default capacitor://localhost.
 * - Patch window.fetch on native: any relative URL starting with /api/
 *   gets prefixed with https://www.vinarai.com so it targets the real
 *   Vercel endpoint. CORS preflight handles the cross-origin call;
 *   the api/*.js handlers' ALLOWED_ORIGINS sets must include
 *   capacitor://localhost.
 *
 * Install BEFORE React mounts so the first auth/profile/API calls
 * benefit from the rewrite. main.jsx calls this once at startup.
 *
 * Web is a no-op — the interceptor only patches when isNative is true.
 */

export function installNativeApiInterceptor() {
  if (typeof window === 'undefined') return
  const isNative = typeof window.Capacitor?.isNativePlatform === 'function'
    && window.Capacitor.isNativePlatform()
  if (!isNative) return

  // Guard against double-install (HMR, repeated calls, etc.). The
  // monkeypatch is idempotent — applying it twice would cause
  // infinite recursion since the second patch would wrap the first.
  if (window.__vinaraiApiInterceptorInstalled) return
  window.__vinaraiApiInterceptorInstalled = true

  const API_BASE = 'https://www.vinarai.com'
  const originalFetch = window.fetch.bind(window)

  window.fetch = function vinaraiPatchedFetch(input, init) {
    let url = typeof input === 'string' ? input : input?.url
    if (typeof url === 'string' && url.startsWith('/api/')) {
      const rewritten = API_BASE + url
      // Reconstruct the Request if the caller passed one — preserving
      // method, headers, body, credentials, etc. via the second-arg
      // Request constructor which copies init from the original.
      if (typeof input === 'string') {
        return originalFetch(rewritten, init)
      }
      try {
        return originalFetch(new Request(rewritten, input), init)
      } catch {
        // Fallback if Request constructor doesn't accept the source
        // (e.g., body already read). Try with bare URL + init.
        return originalFetch(rewritten, init)
      }
    }
    return originalFetch(input, init)
  }
}
